import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  QrCode2,
  ContentCopy,
  Security,
  CheckCircle,
} from '@mui/icons-material';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const TwoFactorSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  const steps = ['Generate QR Code', 'Verify Setup', 'Save Backup Codes'];

  useEffect(() => {
    generateQRCode();
  }, []);

  const generateQRCode = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/auth/2fa/generate`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { secret, otpauthUrl } = response.data;
      setSecret(secret);
      setOtpauthUrl(otpauthUrl);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/auth/2fa/verify`,
        { token: verificationCode },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { backupCodes } = response.data;
      setBackupCodes(backupCodes || []);
      setSuccess('2FA has been successfully enabled!');
      setActiveStep(2);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    setSuccess('Secret key copied to clipboard');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleCopyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setSuccess('Backup codes copied to clipboard');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleComplete = () => {
    navigate('/dashboard');
  };

  const handleSkip = () => {
    // Allow users to skip 2FA setup for now
    navigate('/dashboard');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Security sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Set Up Two-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enhance your account security with 2FA
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Step Content */}
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {activeStep === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Step 1: Scan QR Code
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Use your authenticator app (Google Authenticator, Authy, etc.) to scan this QR code:
                  </Typography>
                  
                  {otpauthUrl && (
                    <Box sx={{ textAlign: 'center', my: 3, p: 2, backgroundColor: 'white', borderRadius: 2 }}>
                      <QRCodeSVG
                        value={otpauthUrl}
                        size={256}
                        level="M"
                        includeMargin={true}
                      />
                    </Box>
                  )}

                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="body2" gutterBottom>
                      Can't scan the QR code? Enter this secret manually:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          backgroundColor: 'grey.100',
                          p: 1,
                          borderRadius: 1,
                          flex: 1,
                          wordBreak: 'break-all',
                        }}
                      >
                        {secret}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={handleCopySecret}
                        sx={{ ml: 1 }}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Paper>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                      variant="outlined"
                      onClick={handleSkip}
                    >
                      Skip for Now
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => setActiveStep(1)}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}

              {activeStep === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Step 2: Verify Setup
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Enter the 6-digit code from your authenticator app to verify the setup:
                  </Typography>

                  <TextField
                    fullWidth
                    label="Verification Code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="000000"
                    inputProps={{
                      maxLength: 6,
                      pattern: '[0-9]*',
                      style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' },
                    }}
                    sx={{ mb: 3 }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                      variant="outlined"
                      onClick={() => setActiveStep(0)}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleVerify}
                      disabled={!verificationCode || verificationCode.length !== 6}
                    >
                      Verify & Enable 2FA
                    </Button>
                  </Box>
                </Box>
              )}

              {activeStep === 2 && (
                <Box>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <CheckCircle sx={{ fontSize: 64, color: 'success.main' }} />
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      2FA Successfully Enabled!
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    Save these backup codes in a safe place. You can use them to access your account if you lose your authenticator device:
                  </Typography>

                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={1}>
                      {backupCodes.map((code, index) => (
                        <Grid item xs={6} key={index}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              backgroundColor: 'grey.100',
                              p: 1,
                              borderRadius: 1,
                              textAlign: 'center',
                            }}
                          >
                            {code}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                    <Button
                      fullWidth
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={handleCopyBackupCodes}
                      sx={{ mt: 2 }}
                    >
                      Copy All Backup Codes
                    </Button>
                  </Paper>

                  <Alert severity="warning" sx={{ mb: 3 }}>
                    Important: These backup codes will not be shown again. Make sure to save them now!
                  </Alert>

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleComplete}
                  >
                    Continue to Dashboard
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default TwoFactorSetupPage;